import type {
  Block, BlockDataMap, BlockOf, BlockType, ButtonStyleId, Colors, DateFormatId, DesignId, Entry, FontId, Group, Photo, Site, TimeFormatId,
} from './types';
import type { IconName } from '../components/Icon';
import { MONTHS, WEEKDAYS, ord, pad, uid, type DateParts } from '../lib/util';
import { isHex } from '../lib/color';

export interface Palette extends Colors { id: string; name: string }
export const PALETTES: Palette[] = [
  { id: 'noir', name: 'Black & gold', bg: '#0F0D0D', ink: '#F3ECE1', accent: '#CDAE74' },
  { id: 'merlot', name: 'Black & wine', bg: '#0F0D0D', ink: '#F3ECE1', accent: '#D0596E' },
  { id: 'burgundy', name: 'Burgundy', bg: '#4A0E1D', ink: '#F7EAE3', accent: '#DDBB7F' },
  { id: 'ink', name: 'Ink', bg: '#F6F3EE', ink: '#1E1E1E', accent: '#1E1E1E' },
  { id: 'sage', name: 'Sage', bg: '#EEF1EA', ink: '#2C3A2E', accent: '#5D7A5A' },
  { id: 'blush', name: 'Blush', bg: '#F9EFEA', ink: '#3E2A29', accent: '#A9675B' },
  { id: 'terracotta', name: 'Terracotta', bg: '#F4E9DE', ink: '#47291D', accent: '#A9512F' },
  { id: 'harbour', name: 'Harbour', bg: '#ECF1F3', ink: '#1D3340', accent: '#3B6B81' },
  { id: 'lilac', name: 'Lilac', bg: '#F2EFF6', ink: '#2E2840', accent: '#6F5E96' },
  { id: 'midnight', name: 'Midnight', bg: '#1D2130', ink: '#F1E9DA', accent: '#CDAA6B' },
  { id: 'forest', name: 'Forest', bg: '#1E2923', ink: '#EDE8DC', accent: '#B9C79A' },
  { id: 'garden', name: 'Garden', bg: '#F7F3EC', ink: '#33402F', accent: '#AD6871' },
  { id: 'paper', name: 'Paper', bg: '#FBFAF7', ink: '#161616', accent: '#B0372A' },
  { id: 'seaglass', name: 'Sea glass', bg: '#EEF4F2', ink: '#173A40', accent: '#2C7A82' },
  { id: 'indigo', name: 'Indigo', bg: '#F4EFE4', ink: '#1E2547', accent: '#2B3A80' },
  { id: 'sunset', name: 'Sunset', bg: '#FFF3EA', ink: '#2A1B3D', accent: '#D9502A' },
];

const SANS = "'Helvetica Neue', Arial, sans-serif";
export interface FontPair { id: FontId; name: string; head: string; body: string; hs: number; bs: number }
export const FONTS: FontPair[] = [
  { id: 'caslon', name: 'Caslon', head: "'Libre Caslon Text', Georgia, serif", body: "'Libre Caslon Text', Georgia, serif", hs: 0.94, bs: 0.95 },
  { id: 'cormorant', name: 'Cormorant & Jost', head: "'Cormorant Garamond', Garamond, Georgia, serif", body: `'Jost', ${SANS}`, hs: 1.14, bs: 1 },
  { id: 'bodoni', name: 'Bodoni & Jost', head: "'Bodoni Moda', Didot, 'Times New Roman', serif", body: `'Jost', ${SANS}`, hs: 1, bs: 1 },
  { id: 'playfair', name: 'Playfair & Karla', head: "'Playfair Display', Georgia, serif", body: `'Karla', ${SANS}`, hs: 1, bs: 1 },
  { id: 'garamond', name: 'EB Garamond', head: "'EB Garamond', Garamond, Georgia, serif", body: "'EB Garamond', Garamond, Georgia, serif", hs: 1.06, bs: 1.08 },
  { id: 'dmserif', name: 'DM Serif & DM Sans', head: "'DM Serif Display', Georgia, serif", body: `'DM Sans', ${SANS}`, hs: 1, bs: 0.98 },
  { id: 'script', name: 'Pinyon Script & EB Garamond', head: "'Pinyon Script', 'Snell Roundhand', 'Apple Chancery', cursive", body: "'EB Garamond', Garamond, Georgia, serif", hs: 1.22, bs: 1.08 },
  { id: 'syne', name: 'Syne & DM Sans', head: `'Syne', ${SANS}`, body: `'DM Sans', ${SANS}`, hs: 0.94, bs: 0.98 },
];

export interface DesignMeta { id: DesignId; name: string; desc: string; palette: string; font: FontId; texture: boolean; variants?: string[] }
export const DESIGNS: DesignMeta[] = [
  { id: 'blacktie', name: 'Black Tie', desc: 'Black or burgundy, with gold detailing', palette: 'noir', font: 'cormorant', texture: false, variants: ['noir', 'merlot', 'burgundy'] },
  { id: 'minimal', name: 'Minimal', desc: 'Oversized names and generous space', palette: 'harbour', font: 'bodoni', texture: false },
  { id: 'arch', name: 'Arch', desc: 'Your favourite photo framed in an arch', palette: 'blush', font: 'cormorant', texture: false },
  { id: 'monogram', name: 'Monogram', desc: 'A classic initial seal, centred and calm', palette: 'sage', font: 'garamond', texture: true },
  { id: 'deco', name: 'Deco', desc: 'Fine gold linework and a fan motif', palette: 'midnight', font: 'playfair', texture: false },
  { id: 'wreath', name: 'Wreath', desc: 'A leafy wreath around your names in script', palette: 'garden', font: 'script', texture: true },
  { id: 'editorial', name: 'Editorial', desc: 'Magazine-style type beside a full-height photo', palette: 'paper', font: 'bodoni', texture: false },
  { id: 'coastal', name: 'Coastal', desc: 'A soft sky, low sun and rolling waves', palette: 'seaglass', font: 'dmserif', texture: false },
  { id: 'indigo', name: 'Indigo', desc: 'Indigo-dyed textile patterns framing your names', palette: 'indigo', font: 'playfair', texture: true },
  { id: 'confetti', name: 'Confetti', desc: 'Bold type with playful, colourful shapes', palette: 'sunset', font: 'syne', texture: false },
];

export const DATE_FORMATS: { id: DateFormatId; f: (p: DateParts) => string }[] = [
  { id: 'ordinal', f: (p) => `${ord(p.d)} ${MONTHS[p.m]} ${p.y}` },
  { id: 'us', f: (p) => `${MONTHS[p.m]} ${p.d}, ${p.y}` },
  { id: 'weekday', f: (p) => `${WEEKDAYS[p.w]} ${ord(p.d)} ${MONTHS[p.m]} ${p.y}` },
  { id: 'dmy', f: (p) => `${pad(p.d)}/${pad(p.m + 1)}/${p.y}` },
  { id: 'mdy', f: (p) => `${pad(p.m + 1)}/${pad(p.d)}/${p.y}` },
  { id: 'dot', f: (p) => `${pad(p.d)}.${pad(p.m + 1)}.${p.y}` },
];
export const TIME_FORMATS: { id: TimeFormatId; sample: string }[] = [{ id: '12', sample: '1:00pm' }, { id: '12U', sample: '1:00 PM' }, { id: '24', sample: '13:00' }];
export const BUTTON_STYLES: { id: ButtonStyleId; label: string }[] = [{ id: 'outline', label: 'Outline, rounded' }, { id: 'solid', label: 'Filled' }, { id: 'square', label: 'Outline, square' }];

export function byId<T extends { id: string }>(arr: T[], id: string | undefined): T {
  return arr.find((x) => x.id === id) || arr[0];
}
export const paletteColors = (id: string): Colors => { const P = byId(PALETTES, id); return { bg: P.bg, ink: P.ink, accent: P.accent }; };

/* ---------- blocks ---------- */
export interface BlockMeta<K extends BlockType> {
  label: string; icon: IconName; desc: string; title: string;
  showTitle?: boolean; single?: boolean;
  data: () => BlockDataMap[K];
}
export const DRESS_PHOTOS = 6;

export const BLOCK_TYPES: { [K in BlockType]: BlockMeta<K> } = {
  text: { label: 'Text', icon: 'text', desc: 'A heading and a few paragraphs', title: 'A note from us', data: () => ({ body: 'Write anything your guests should know, like a thank-you or a little about the day.' }) },
  gallery: { label: 'Gallery', icon: 'image', desc: 'A row or grid of photos', title: 'Gallery', showTitle: false, data: () => ({ layout: 'strip', images: [], caption: '' }) },
  countdown: { label: 'Countdown', icon: 'clock', desc: 'Days, hours and minutes to the wedding', title: 'Wedding Countdown', data: () => ({ message: 'until we say I do' }) },
  story: {
    label: 'Love story', icon: 'heart', desc: 'How you met, in your own words', title: 'Our Love Story',
    data: () => ({ body: 'We met in the queue for a bookshop’s closing-down sale, both reaching for the last copy of the same cookbook. Jemila let Jembo have it on one condition: that he cook her something from it.\n\nEight years, a great many burnt dinners and one very nervous proposal later, we’re getting married, and we’d love you to be there.', image: null }),
  },
  schedule: {
    label: 'Schedule', icon: 'list', desc: 'The order of the day, with times', title: 'Schedule',
    data: () => ({
      events: [
        { time: '14:30', name: 'Guests arrive', place: 'The garden lawn', notes: '' },
        { time: '15:00', name: 'Ceremony', place: 'Beneath the old fig tree', notes: 'Unplugged. We’d love to see your faces, not your phones.' },
        { time: '16:00', name: 'Drinks & canapés', place: 'The terrace', notes: '' },
        { time: '18:00', name: 'Dinner & speeches', place: 'The Glasshouse', notes: '' },
        { time: '23:00', name: 'Carriages', place: '', notes: 'Shuttles run back to town until midnight.' },
      ],
    }),
  },
  venue: { label: 'Venue & map', icon: 'pin', desc: 'Address, map and directions', title: 'Venue & Directions', data: () => ({ name: '', address: '', notes: 'Free parking on site. Follow the lanterns from the gate to the garden.', mapLink: '', showMap: true }) },
  dresscode: {
    label: 'Dress code', icon: 'hanger', desc: 'Colours, attire and outfit photos', title: 'Dress Code',
    data: () => ({
      style: 'Black tie or traditional attire',
      body: 'Dress to celebrate! Our colours are emerald and gold, with champagne as an accent. Aso-ebi is available, so message us before 1st August to order yours.',
      colors: [{ hex: '#1F5C4A', name: 'Emerald' }, { hex: '#C9A45C', name: 'Gold' }, { hex: '#E9DCC0', name: 'Champagne' }],
      images: [],
    }),
  },
  accommodation: {
    label: 'Accommodation', icon: 'bed', desc: 'Places to stay nearby', title: 'Accommodation Information',
    data: () => ({
      intro: 'We’ve held a few rooms at these places nearby. Mention our names when you book.',
      places: [
        { name: 'The Lagoon Inn', distance: '5 minutes’ drive', notes: 'Cosy rooms above the old pub, with breakfast included.', link: '' },
        { name: 'Marina House', distance: '10 minutes’ walk', notes: 'Six rooms in a converted farmhouse. Book early.', link: '' },
      ],
    }),
  },
  party: {
    label: 'Wedding party', icon: 'people', desc: 'The people standing beside you', title: 'Wedding Party',
    data: () => ({
      intro: '',
      people: [
        { name: 'Ada Okafor', role: 'Maid of honour', note: 'Jemila’s sister and keeper of every secret.' },
        { name: 'Tunde Bello', role: 'Best man', note: 'Jembo’s friend since the first day of school.' },
        { name: 'Nneka Eze', role: 'Bridesmaid', note: '' },
        { name: 'Femi Adeyemi', role: 'Groomsman', note: '' },
      ],
    }),
  },
  fund: { label: 'Honeymoon fund', icon: 'gift', desc: 'Gifts, registry or a fund link', title: 'Our Honeymoon Fund', data: () => ({ body: 'Your presence is the best gift. If you’d like to give something more, we’re saving for a honeymoon on the coast.', buttonLabel: 'Contribute', link: '', details: '' }) },
  faq: {
    label: 'FAQ', icon: 'help', desc: 'Answers to common questions', title: 'Frequently Asked Questions',
    data: () => ({
      items: [
        { q: 'What should I wear?', a: 'Black tie or traditional attire in our colours. The dress code section has the details and some ideas.' },
        { q: 'Can I bring a plus-one?', a: 'If your invitation names a guest, absolutely. Otherwise we’re keeping numbers small.' },
        { q: 'Are children welcome?', a: 'We love your little ones, but this will be an adults-only celebration.' },
        { q: 'Is there parking?', a: 'Yes, there’s free parking at the venue, and shuttles back to town until midnight.' },
      ],
    }),
  },
  photos: { label: 'Photo sharing', icon: 'camera', desc: 'A hashtag and an upload link', title: 'Share your photos with us', data: () => ({ body: 'We’d love to see the day through your eyes. Tag your photos or add them to our shared album.', hashtag: '#JemboAndJemila', buttonLabel: 'Add to the album', link: '' }) },
  rsvp: { label: 'RSVP', icon: 'mail', desc: 'A button that opens your reply form', title: 'RSVP', single: true, data: () => ({ intro: 'We’d love to know if you can make it.', buttonLabel: 'RSVP now', deadline: '2027-08-01', allowPlusOne: true, askKids: true, maxKids: 6, askMeal: true, meals: 'Beef\nFish\nVegetarian', askDietary: true, askSong: true }) },
};
export const BLOCK_ORDER = Object.keys(BLOCK_TYPES) as BlockType[];

export const LIST_TEMPLATES = {
  events: () => ({ time: '12:00', name: 'New moment', place: '', notes: '' }),
  places: () => ({ name: 'New place to stay', distance: '', notes: '', link: '' }),
  people: () => ({ name: 'New person', role: '', note: '' }),
  items: () => ({ q: 'New question', a: '' }),
  colors: () => ({ hex: '#C9A45C', name: '' }),
};

type BlockOverrides<T extends BlockType> = Partial<Omit<BlockOf<T>, 'data' | 'type' | 'kind'>> & { data?: Partial<BlockDataMap[T]> };
export function makeBlock<T extends BlockType>(type: T, over: BlockOverrides<T> = {}): BlockOf<T> {
  const M = BLOCK_TYPES[type] as BlockMeta<T>;
  const { data, ...rest } = over;
  return {
    kind: 'block', id: uid('b'), type, title: M.title, showTitle: M.showTitle !== false, hidden: false,
    ...rest,
    data: { ...M.data(), ...(data || {}) },
  } as BlockOf<T>;
}
export function makeGroup(title: string, items: Block[] = [], over: Partial<Group> = {}): Group {
  return { kind: 'group', id: uid('g'), title, hidden: false, collapsed: false, items, ...over };
}

export const GROUP_TEMPLATES: { title: string; icon: IconName; desc: string; make: () => Block[] }[] = [
  { title: 'Our love story', icon: 'heart', desc: 'Your story with a photo gallery', make: () => [makeBlock('story'), makeBlock('gallery', { data: { layout: 'feature' } })] },
  { title: 'Schedule', icon: 'list', desc: 'The order of the day', make: () => [makeBlock('schedule')] },
  { title: 'Venue & map', icon: 'pin', desc: 'Address, map and directions', make: () => [makeBlock('venue')] },
  { title: 'Dress code', icon: 'hanger', desc: 'Colours, attire and outfit photos', make: () => [makeBlock('dresscode')] },
  { title: 'Travel & stay', icon: 'pin', desc: 'Venue directions and places to stay', make: () => [makeBlock('venue'), makeBlock('accommodation', { title: 'Where to Stay' })] },
  { title: 'People', icon: 'people', desc: 'Introduce your wedding party', make: () => [makeBlock('party')] },
  { title: 'Gifts', icon: 'gift', desc: 'Registry or honeymoon fund', make: () => [makeBlock('fund')] },
  { title: 'FAQ', icon: 'help', desc: 'Kids, parking and more', make: () => [makeBlock('faq')] },
  { title: 'Gallery', icon: 'image', desc: 'A grid of your favourite photos', make: () => [makeBlock('gallery', { data: { layout: 'grid' } })] },
  { title: 'Photo sharing', icon: 'camera', desc: 'Hashtag and shared album link', make: () => [makeBlock('photos')] },
  { title: 'Countdown', icon: 'clock', desc: 'A live countdown to the day', make: () => [makeBlock('countdown')] },
  { title: 'New group', icon: 'folder', desc: 'Start empty and add blocks', make: () => [] },
];

/* ---------- the sample website every new site starts from ---------- */
export function defaultSite(): Site {
  return {
    v: 2,
    design: { base: 'blacktie', palette: 'noir', colors: paletteColors('noir'), font: 'cormorant', dateFormat: 'ordinal', timeFormat: '12', button: 'outline' },
    header: { name1: 'Jembo', name2: 'Jemila', intro: 'The wedding of', date: '2027-09-24', time: '15:00', venue: 'The Garden Pavilion', city: 'Lagos' },
    entries: [
      makeBlock('gallery'),
      makeGroup('Wedding countdown', [makeBlock('countdown')]),
      makeGroup('Our love story', [makeBlock('story'), makeBlock('gallery', { data: { layout: 'feature' } })]),
      makeBlock('schedule'),
      makeBlock('venue', { data: { address: 'Ozumba Mbadiwe Avenue\nVictoria Island, Lagos' } }),
      makeBlock('dresscode'),
      makeBlock('accommodation', { hidden: true }),
      makeGroup('People', [makeBlock('party')], { hidden: true }),
      makeGroup('Honeymoon fund', [makeBlock('fund')], { hidden: true }),
      makeGroup('FAQ', [makeBlock('gallery', { data: { layout: 'grid' } }), makeBlock('faq')]),
      makeGroup('Photo sharing', [makeBlock('photos')], { hidden: true }),
      makeBlock('rsvp'),
    ],
    footer: { message: 'We can’t wait to celebrate with you.', hashtag: '#JemboAndJemila', showMade: true },
  };
}

/** The sample website with the couple's own names and date filled in. */
export function namedSample(n1: string, n2: string, date?: string): Site {
  const a = String(n1 || '').trim() || 'Partner one';
  const b = String(n2 || '').trim() || 'Partner two';
  const j = (x: string) => JSON.stringify(x).slice(1, -1);
  const s = JSON.parse(JSON.stringify(defaultSite()).replace(/Jembo/g, j(a)).replace(/Jemila/g, j(b))) as Site;
  const tag = '#' + (a + 'And' + b).replace(/[^\p{L}\p{N}]+/gu, '');
  s.footer.hashtag = tag;
  allBlocks(s).forEach(({ b: blk }) => { if (blk.type === 'photos') blk.data.hashtag = tag; });
  if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) s.header.date = date;
  return s;
}

/** Fills anything missing from a stored website, so older or damaged data still opens. */
export function normalizeSite(raw: unknown): Site {
  const d = defaultSite();
  if (!raw || typeof raw !== 'object' || !Array.isArray((raw as Site).entries)) return d;
  const s = raw as Site;
  const design = { ...d.design, ...(s.design || {}) };
  if ((design.base as string) === 'botanical') { design.base = 'blacktie'; if (!design.palette || design.palette === 'ink') Object.assign(design, { palette: 'noir', colors: paletteColors('noir'), font: 'cormorant' }); }
  if (!DESIGNS.some((x) => x.id === design.base)) design.base = 'blacktie';
  const col = { ...d.design.colors, ...((s.design && s.design.colors) || {}) };
  (Object.keys(d.design.colors) as (keyof Colors)[]).forEach((k) => { if (!isHex(col[k])) col[k] = d.design.colors[k]; });
  design.colors = { bg: col.bg, ink: col.ink, accent: col.accent };
  if (!FONTS.some((f) => f.id === design.font)) design.font = d.design.font;
  if (!DATE_FORMATS.some((f) => f.id === design.dateFormat)) design.dateFormat = d.design.dateFormat;
  if (!TIME_FORMATS.some((f) => f.id === design.timeFormat)) design.timeFormat = d.design.timeFormat;
  if (!BUTTON_STYLES.some((f) => f.id === design.button)) design.button = d.design.button;
  const photo = (p: unknown): Photo | null => {
    const x = p as Photo | null;
    return x && typeof x === 'object' && typeof x.url === 'string' && typeof x.id === 'string' ? x : null;
  };
  const fixBlock = (b: Block | null | undefined): Block | null => {
    if (!b || typeof b !== 'object' || !Object.prototype.hasOwnProperty.call(BLOCK_TYPES, b.type)) return null;
    const M = BLOCK_TYPES[b.type as BlockType] as BlockMeta<BlockType>;
    const defaults = M.data() as Record<string, unknown>;
    const data = { ...defaults, ...(b.data && typeof b.data === 'object' ? b.data : {}) } as Record<string, unknown>;
    // Each field keeps the type the editor and renderer expect
    Object.keys(defaults).forEach((k) => {
      const want = defaults[k], v = data[k];
      if (Array.isArray(want)) data[k] = Array.isArray(v) ? v.filter((x) => x && typeof x === 'object') : [];
      else if (typeof want === 'string' && typeof v !== 'string') data[k] = v == null ? '' : String(v);
      else if (typeof want === 'boolean') data[k] = !!v;
      else if (typeof want === 'number') data[k] = Number.isFinite(Number(v)) ? Number(v) : want;
    });
    if (b.type === 'gallery' || b.type === 'dresscode') data.images = Array.isArray(data.images) ? data.images.map(photo).filter(Boolean) : [];
    if (b.type === 'story') data.image = photo(data.image);
    if (b.type === 'dresscode' && !Array.isArray(data.colors)) data.colors = [];
    return { ...b, kind: 'block', id: b.id || uid('b'), title: b.title ?? M.title, showTitle: b.showTitle ?? M.showTitle !== false, hidden: !!b.hidden, data } as Block;
  };
  const entries = s.entries.map((e): Entry | null => {
    if (!e) return null;
    if (e.kind === 'group') return { ...e, id: e.id || uid('g'), title: String(e.title || ''), items: (Array.isArray(e.items) ? e.items : []).map(fixBlock).filter((x): x is Block => !!x) };
    return fixBlock(e as Block);
  }).filter((x): x is Entry => !!x);
  const header = { ...d.header, ...(s.header || {}) };
  (Object.keys(d.header) as (keyof typeof header)[]).forEach((k) => { if (typeof header[k] !== 'string') header[k] = d.header[k]; });
  const footer = { ...d.footer, ...(s.footer || {}) };
  if (typeof footer.message !== 'string') footer.message = '';
  if (typeof footer.hashtag !== 'string') footer.hashtag = '';
  footer.showMade = footer.showMade !== false;
  return { v: 2, design, header, footer, entries };
}

/* ---------- lookups ---------- */
export interface BlockHit { block: Block; list: Block[] | Entry[]; index: number; group: Group | null }
export function findBlock(s: Site, id: string): BlockHit | null {
  for (let i = 0; i < s.entries.length; i++) {
    const e = s.entries[i];
    if (e.kind === 'block' && e.id === id) return { block: e, list: s.entries, index: i, group: null };
    if (e.kind === 'group') {
      const j = e.items.findIndex((b) => b.id === id);
      if (j > -1) return { block: e.items[j], list: e.items, index: j, group: e };
    }
  }
  return null;
}
export const findGroup = (s: Site, id: string) => (s.entries.find((e) => e.kind === 'group' && e.id === id) as Group | undefined) || null;
export function allBlocks(s: Site) {
  const out: { b: Block; entry: Entry }[] = [];
  s.entries.forEach((e) => { if (e.kind === 'block') out.push({ b: e, entry: e }); else e.items.forEach((b) => out.push({ b, entry: e })); });
  return out;
}
const blockVisible = (b: Block, entry: Entry) => !b.hidden && !entry.hidden;
export function firstVisibleOfType<T extends BlockType>(s: Site, type: T): BlockOf<T> | null {
  const hit = allBlocks(s).find(({ b, entry }) => b.type === type && blockVisible(b, entry));
  return hit ? (hit.b as BlockOf<T>) : null;
}
export const hasRsvp = (s: Site) => allBlocks(s).some(({ b }) => b.type === 'rsvp');
export function blockPhotos(b: Block): Photo[] {
  if (b.type === 'gallery' || b.type === 'dresscode') return b.data.images || [];
  if (b.type === 'story' && b.data.image) return [b.data.image];
  return [];
}
export const visibleEntries = (s: Site) => s.entries.filter((e) => !e.hidden && (e.kind === 'block' || e.items.some((b) => !b.hidden)));
export const siteTitle = (s: Site | null | undefined) => {
  const h = s && s.header;
  return (h ? [h.name1, h.name2].filter(Boolean).join(' & ') || 'Untitled website' : 'Untitled website').slice(0, 200);
};
